import React, { useCallback, useEffect } from "react";
import Grid from "@carrier-io/air-react/Grid";
import Typography from "@carrier-io/air-react/Typography";
import Box from "@carrier-io/air-react/Box";
import { SubmitButton } from "../../components/submitButton/SubmitButton";
import { Clients, useMutation } from "../../apollo";
import { AWSRegions } from "../../generated/ups/graphql";
import { ConfirmForgotPassword } from "../../graphql/mutations/confirmForgotPassword";
import { SetNewPasswordInput } from "../../validations/setNewPasswordValidation";
import { useNavigate, useSearchParams } from "react-router-dom";
import { AlertTypes } from "../../contexts/alert/types";
import { useAlerts } from "../../contexts/alert/AlertsContext";
import { RoutesPage } from "../../constants/routes";
import { EmailChangeAccount } from "../../components/emailChangeAccount/emailChangeAccount";
import { SetNewPasswordForm } from "../../components/setNewPasswordForm/SetNewPasswordForm";

export const ForgotPasswordPage = () => {
  const { pushAlert } = useAlerts();
  const [runConfirmForgotPassword, confirmForgotPassword] = useMutation<
    { confirmForgotPassword: { message: string } },
    {
      username: string;
      email: string;
      code: string;
      newPassword: string;
      region: AWSRegions;
    }
  >(Clients.Authn, ConfirmForgotPassword);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const code = searchParams.get("code");
  const username = searchParams.get("username");
  const email = searchParams.get("email");
  const region = searchParams.get("region");
  const expireAt = Number(searchParams.get("expireAt"));

  const handleForgotPassword = async (data: SetNewPasswordInput) => {
    try {
      const result = await runConfirmForgotPassword({
        variables: {
          username: username ?? "",
          email: email || "",
          code: code || "",
          newPassword: data.password,
          region: (region as AWSRegions) || AWSRegions.US_EAST_1,
        },
      });

      if (result.data?.confirmForgotPassword?.message) {
        pushAlert(AlertTypes.SUCCESS, "Password changed successfully");
        navigate(RoutesPage.Login);
      }
    } catch (error) {
      pushAlert(AlertTypes.ERROR, "Failed to set new password");
    }
  };

  const isLoading = confirmForgotPassword.loading;
  const isLinkMalformed = !email || !username || !code || !region;
  const isLinkExpired = expireAt < Date.now();
  const isLinkFail = isLinkMalformed || isLinkExpired;

  useEffect(() => {
    if (isLinkFail) {
      const params = new URLSearchParams(searchParams);

      if (isLinkExpired) {
        params.set(
          "prevError",
          "This password set link has expired. Please request a new link.",
        );
      }
      if (isLinkMalformed) {
        params.set(
          "prevError",
          "Your reset password link was malformed. Please, try again or request another reset password link.",
        );
      }

      navigate(
        {
          pathname: RoutesPage.Login,
          search: params.toString(),
        },
        { replace: true },
      );
    }
  }, [
    isLinkFail,
    navigate,
    searchParams,
    username,
    region,
    isLinkExpired,
    isLinkMalformed,
  ]);

  const renderSubmitButton = useCallback(
    (isValid: boolean) => (
      <SubmitButton isLoading={isLoading} disabled={!isValid || isLoading}>
        Save new password
      </SubmitButton>
    ),
    [isLoading],
  );
  return (
    <>
      <Grid>
        <Typography variant="body2">Reset your password</Typography>
      </Grid>
      <Box maxWidth="338px" width="100%">
        <Box marginTop="20px" width="100%">
          <EmailChangeAccount email={email ?? ""} />
        </Box>
        <SetNewPasswordForm
          onSubmit={handleForgotPassword}
          passwordLabel="New password"
          submitButton={renderSubmitButton}
        />
      </Box>
    </>
  );
};
